/**
 * Subscriptions Component (September 2025)
 *
 * Manage recurring credit subscriptions with automated daily renewals.
 * Displays active, canceled, and paused subscriptions with management controls.
 */

import { useState } from 'react';
import { useQuery as useConvexQuery } from 'convex/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../convex/_generated/api';
import { CreateSubscriptionModal } from './CreateSubscriptionModal';
import { SubscriptionCard } from './SubscriptionCard';
import { getConvexHttpUrl } from '../utils/convex';

interface Subscription {
  id: string;
  amount: number;
  validityDays: number;
  status: 'active' | 'canceled' | 'paused' | 'expired';
  currentCycleStart: number;
  currentCycleEnd: number;
  nextRenewalAt: number;
  renewalCount?: number;
  createdAt?: number;
}

interface SubscriptionStatusResponse {
  success: boolean;
  subscriptions: Subscription[];
}

export function Subscriptions() {
  const { user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Fetch service key using Convex React query (same pattern as Dashboard)
  const serviceKeyData = useConvexQuery(api.users.getUserServiceKey);
  const serviceKey = serviceKeyData?.serviceApiKey ?? '';

  // Get Convex HTTP API URL from environment
  const convexHttpUrl = getConvexHttpUrl();

  // Fetch subscriptions
  const { data: subscriptionsData, isLoading: subscriptionsLoading, error: subscriptionsError } = useQuery<SubscriptionStatusResponse>({
    queryKey: ['subscriptions', clerkUser?.id],
    queryFn: async () => {
      if (!serviceKey || !clerkUser) {
        throw new Error('Service key or user not available');
      }

      const response = await fetch(
        `${convexHttpUrl}/api/subscriptions/status?serviceApiKey=${serviceKey}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch subscriptions: ${response.statusText}`);
      }

      return response.json();
    },
    enabled: !!serviceKey && !!clerkUser,
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async (subscriptionId: string) => {
      const response = await fetch(
        `${convexHttpUrl}/api/subscriptions/stop`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            serviceApiKey: serviceKey,
            subscriptionId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to cancel subscription: ${response.statusText}`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  const handleCancelSubscription = (subscription: Subscription) => {
    if (confirm(`Cancel subscription for ${subscription.amount} credits every ${subscription.validityDays} days? Credits remain active until end of current cycle.`)) {
      cancelSubscriptionMutation.mutate(subscription.id);
    }
  };

  const activeSubscriptions = subscriptionsData?.subscriptions?.filter(s => s.status === 'active') ?? [];
  const inactiveSubscriptions = subscriptionsData?.subscriptions?.filter(s => s.status !== 'active') ?? [];

  if (!clerkLoaded) {
    return (
      <div className="min-h-screen bg-terminal-black flex items-center justify-center">
        <div className="text-terminal-orange terminal-glow text-xl">Loading authentication...</div>
      </div>
    );
  }

  if (!clerkUser) {
    return (
      <div className="min-h-screen bg-terminal-black flex items-center justify-center">
        <div className="text-red-400 text-xl">Please sign in to manage subscriptions</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-terminal-black p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-terminal-orange terminal-glow mb-2">Subscription Management</h1>
          <p className="text-terminal-cyan">
            Manage recurring credit subscriptions with automated daily renewals
          </p>
        </div>

        {/* Create Subscription Button */}
        <div className="mb-8">
          <button
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!serviceKey}
            className="terminal-button px-6 py-3 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            + Create New Subscription
          </button>
        </div>

        {/* Loading State */}
        {subscriptionsLoading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-terminal-orange"></div>
            <p className="text-terminal-cyan mt-4">Loading subscriptions...</p>
          </div>
        )}

        {/* Error State */}
        {subscriptionsError && (
          <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-6">
            <p className="text-red-400">
              Error loading subscriptions: {(subscriptionsError as Error).message}
            </p>
          </div>
        )}

        {/* Active Subscriptions */}
        {!subscriptionsLoading && activeSubscriptions.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-terminal-orange terminal-glow mb-4">
              Active Subscriptions ({activeSubscriptions.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeSubscriptions.map((subscription) => (
                <SubscriptionCard
                  key={subscription.id}
                  subscription={subscription}
                  onCancel={handleCancelSubscription}
                  isCanceling={cancelSubscriptionMutation.isPending}
                />
              ))}
            </div>
          </div>
        )}

        {/* Inactive Subscriptions */}
        {!subscriptionsLoading && inactiveSubscriptions.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-terminal-cyan mb-4">
              Inactive Subscriptions ({inactiveSubscriptions.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {inactiveSubscriptions.map((subscription) => (
                <SubscriptionCard
                  key={subscription.id}
                  subscription={subscription}
                  onCancel={handleCancelSubscription}
                  isCanceling={false}
                />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!subscriptionsLoading && !subscriptionsError && (subscriptionsData?.subscriptions?.length === 0 || !subscriptionsData) && (
          <div className="terminal-box p-12 text-center">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-2xl font-bold text-terminal-orange terminal-glow mb-2">No Subscriptions Yet</h3>
            <p className="text-terminal-cyan mb-6">
              Create your first recurring credit subscription to automate credit renewals
            </p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="terminal-button px-6 py-3 font-semibold"
            >
              Create Your First Subscription
            </button>
          </div>
        )}

        {/* Create Subscription Modal */}
        <CreateSubscriptionModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          serviceKey={serviceKey}
          onSubscriptionCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
          }}
        />
      </div>
    </div>
  );
}
